import { useState, useRef } from 'react';
import type { Tour } from '../../../mocks/tours';
import { useCart } from '../../../hooks/useCart';
import { saveReservation } from '../../../utils/reservationStorage';
import type { Reservation } from '../../../mocks/reservations';

interface Props {
  tour: Tour;
}

export default function ReservationForm({ tour }: Props) {
  const { addToCart, isInCart } = useCart();
  const [added, setAdded] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [charCount, setCharCount] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);
  const inCart = isInCart(tour.id);

  const handleAdd = () => {
    addToCart(tour);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    setSubmitting(true);
    setErrorMsg('');

    const reservation: Reservation = {
      id: `r_${Date.now()}`,
      tourId: tour.id,
      tourName: tour.name,
      firstName: (data.get('firstName') as string) || '',
      lastName: (data.get('lastName') as string) || '',
      email: (data.get('email') as string) || '',
      phone: (data.get('phone') as string) || '',
      departureDate: (data.get('departureDate') as string) || '',
      adults: Number(data.get('adults')) || 1,
      children: Number(data.get('children')) || 0,
      specialRequests: (data.get('specialRequests') as string) || '',
      status: 'beklemede',
      createdAt: new Date().toISOString(),
      totalPrice: tour.price,
      currency: tour.currency,
    };

    try {
      // Supabase'e kaydet — ana işlem
      await saveReservation(reservation);

      // Readdy form endpoint'e de gönder (bağımsız, başarısız olsa da devam eder)
      const params = new URLSearchParams();
      data.forEach((val, key) => params.append(key, val as string));
      fetch('https://readdy.ai/api/form/d70le5v9hgr6oot2r120', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      }).catch(() => {});

      setSubmitted(true);
      formRef.current?.reset();
      setCharCount(0);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Bilinmeyen hata';
      setErrorMsg(`Rezervasyon gönderilemedi: ${msg}. Lütfen tekrar deneyin.`);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-2xl p-7 border border-gray-100 sticky top-24 text-center">
        <div className="w-16 h-16 flex items-center justify-center bg-green-50 rounded-full mx-auto mb-4">
          <i className="ri-check-double-line text-green-500 text-3xl" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">Rezervasyon Talebiniz Alındı!</h3>
        <p className="text-sm text-gray-500 mb-6">
          En kısa sürede ekibimiz sizinle iletişime geçecektir.
        </p>
        <button
          onClick={() => setSubmitted(false)}
          className="w-full py-2.5 rounded-xl border border-orange-200 text-orange-500 text-sm font-medium hover:bg-orange-50 transition-colors cursor-pointer whitespace-nowrap"
        >
          Yeni Rezervasyon Talebi
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 sticky top-24 overflow-hidden">
      {/* Price Header */}
      <div className="bg-orange-500 p-6 text-white">
        <p className="text-orange-100 text-xs mb-1">Kişi Başı Başlangıç Fiyatı</p>
        <p className="text-3xl font-bold mb-1">
          {tour.currency === 'TL' ? `₺${tour.price.toLocaleString('tr-TR')}` : `€${tour.price.toLocaleString('tr-TR')}`}
        </p>
        <div className="flex items-center gap-4 text-orange-100 text-xs mt-2">
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line" />
            {tour.duration} {tour.durationUnit}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-map-pin-line" />
            Ankara Çıkışlı
          </span>
        </div>
      </div>

      {/* Add to cart */}
      <div className="px-6 pt-5">
        <button
          onClick={handleAdd}
          disabled={inCart || added}
          className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition-all duration-300 cursor-pointer whitespace-nowrap ${inCart || added ? 'bg-green-500 text-white' : 'bg-orange-50 hover:bg-orange-100 text-orange-500 border border-orange-200'}`}
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className={`${inCart || added ? 'ri-check-line' : 'ri-shopping-cart-add-line'} text-base`} />
          </div>
          <span>{inCart || added ? 'Sepete Eklendi' : 'Sepete Ekle'}</span>
        </button>
      </div>

      {/* Divider */}
      <div className="flex items-center gap-3 px-6 py-4">
        <div className="flex-1 h-px bg-gray-100" />
        <span className="text-xs text-gray-400 font-medium">veya rezervasyon talebi oluştur</span>
        <div className="flex-1 h-px bg-gray-100" />
      </div>

      {/* Form */}
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        data-readdy-form
        className="px-6 pb-6 space-y-3"
      >
        <input type="hidden" name="tourName" value={tour.name} />
        <input type="hidden" name="tourId" value={tour.id} />

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Ad *</label>
            <input
              name="firstName"
              type="text"
              required
              placeholder="Adınız"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-orange-400 transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Soyad *</label>
            <input
              name="lastName"
              type="text"
              required
              placeholder="Soyadınız"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-orange-400 transition-colors"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">E-posta *</label>
          <input
            name="email"
            type="email"
            required
            placeholder="ornek@mail.com"
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-orange-400 transition-colors"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Telefon *</label>
          <input
            name="phone"
            type="tel"
            required
            placeholder="0532 XXX XX XX"
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-orange-400 transition-colors"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Tercih Ettiğiniz Tarih</label>
          <input
            name="departureDate"
            type="date"
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-orange-400 transition-colors"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Yetişkin</label>
            <select
              name="adults"
              defaultValue="1"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-orange-400 transition-colors cursor-pointer"
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                <option key={n} value={n}>{n} Yetişkin</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Çocuk</label>
            <select
              name="children"
              defaultValue="0"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-orange-400 transition-colors cursor-pointer"
            >
              {[0, 1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={n}>{n} Çocuk</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Özel İstekler
            <span className={`ml-1 ${charCount > 450 ? 'text-red-400' : 'text-gray-400'}`}>({charCount}/500)</span>
          </label>
          <textarea
            name="specialRequests"
            rows={3}
            maxLength={500}
            onChange={(e) => setCharCount(e.target.value.length)}
            placeholder="Özel istekleriniz, sorularınız..."
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-orange-400 transition-colors resize-none"
          />
        </div>

        {errorMsg && (
          <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
            <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-error-warning-line text-red-500 text-sm" />
            </div>
            <p className="text-xs text-red-600">{errorMsg}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white py-3 rounded-xl font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
        >
          {submitting ? (
            <>
              <i className="ri-loader-4-line animate-spin" />
              <span>Gönderiliyor...</span>
            </>
          ) : (
            <>
              <i className="ri-send-plane-line" />
              <span>Rezervasyon Talebi Gönder</span>
            </>
          )}
        </button>

        <p className="text-center text-xs text-gray-400">
          Bilgileriniz gizli tutulmakta, üçüncü taraflarla paylaşılmamaktadır.
        </p>
      </form>
    </div>
  );
}
